import axios from 'axios';

const ENV_API = process.env.REACT_APP_BACKEND_URL || '';

// Smart backend URL detection:
// On the deployed custom domain (app-bigdemenagement.fr), the frontend was built
// with the Emergent default backend URL (moving-manager-hub.emergent.host) which
// causes third-party cookie blocking. We detect this at runtime and use the
// current site's origin instead, so cookies are first-party.
function detectApiBaseUrl() {
    if (typeof window === 'undefined') return ENV_API;
    const origin = window.location.origin;
    // If the env points to a different host than the one the user is on,
    // and the env is an Emergent default backend URL, use the current origin.
    try {
        const envHost = new URL(ENV_API).host;
        const currentHost = window.location.host;
        if (envHost !== currentHost) {
            // Always prefer the current origin when running in a browser:
            // - Avoids cross-site cookie blocking
            // - Works across preview, custom domain, localhost
            return origin;
        }
    } catch {
        // ENV_API is empty or invalid → fall back to current origin
        return origin;
    }
    return ENV_API;
}

export const API_BASE_URL = detectApiBaseUrl();

// Apply globally so every axios call sends cookies
axios.defaults.withCredentials = true;

// Rewrite any absolute URL pointing to the wrong backend host
axios.interceptors.request.use((config) => {
    if (config.url && /^https?:\/\//i.test(config.url)) {
        try {
            const u = new URL(config.url);
            const current = new URL(API_BASE_URL);
            if (u.host !== current.host) {
                u.protocol = current.protocol;
                u.host = current.host;
                config.url = u.toString();
            }
        } catch {
            // ignore malformed URLs
        }
    }
    return config;
});

let isRefreshing = false;
let refreshSubscribers = [];
let refreshFailedAt = 0; // timestamp; we skip refresh attempts for 5s after a failure to avoid loops

function onRefreshed() {
    refreshSubscribers.forEach((cb) => cb());
    refreshSubscribers = [];
}

function addRefreshSubscriber(cb) {
    refreshSubscribers.push(cb);
}

let onUnauthenticated = null;

export function setOnUnauthenticated(handler) {
    onUnauthenticated = handler;
}

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config || {};
        const status = error.response?.status;
        const url = originalRequest.url || '';

        const isAuthMe = url.includes('/api/auth/me');
        const isAuthLogin = url.includes('/api/auth/login');
        const isAuthRefresh = url.includes('/api/auth/refresh');
        const isAuthLogout = url.includes('/api/auth/logout');
        const isAuthEndpoint = isAuthLogin || isAuthRefresh || isAuthLogout;

        // Skip refresh logic if we recently failed (avoid logout loops)
        const recentlyFailed = Date.now() - refreshFailedAt < 5000;

        if (status === 401 && !originalRequest._retry && !isAuthEndpoint && !recentlyFailed) {
            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    addRefreshSubscriber(() => {
                        axios(originalRequest).then(resolve).catch(reject);
                    });
                });
            }

            isRefreshing = true;
            try {
                await axios.post(`${API_BASE_URL}/api/auth/refresh`, {}, { withCredentials: true });
                isRefreshing = false;
                refreshFailedAt = 0;
                onRefreshed();
                return axios(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                refreshSubscribers = [];
                refreshFailedAt = Date.now();
                if (isAuthMe && onUnauthenticated) {
                    onUnauthenticated();
                }
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default axios;
